import { supabase } from '../lib/supabase';
import blsApiService from './blsApiService';
import onetApiService from './onetApiService';

class OccupationUpdateService {
  constructor() {
    this.blsApi = blsApiService;
    this.onetApi = onetApiService;
  }

  /**
   * Update occupation data from O*NET API
   * @param {string} occupationId - Database occupation ID
   * @param {string} socCode - SOC occupation code for O*NET lookup
   */
  async updateOccupationFromOnet(occupationId, socCode) {
    try {
      console.log(`Updating occupation ${occupationId} with O*NET data for SOC code ${socCode}`);
      
      // Get comprehensive O*NET data
      const onetResult = await this.onetApi.getComprehensiveOccupationData(socCode);
      
      if (!onetResult.success) {
        console.error('Failed to fetch O*NET data:', onetResult.error);
        return { success: false, error: onetResult.error };
      }

      // Process O*NET data
      const processedData = this.onetApi.processOnetDataForDatabase(onetResult.data);
      
      // Update database
      const updateResult = await this.updateOccupationInDatabase(occupationId, processedData);
      
      if (!updateResult.success) {
        return updateResult;
      }

      console.log(`Successfully updated occupation ${occupationId} with O*NET data`);
      return {
        success: true,
        data: updateResult.data,
        message: 'Occupation updated successfully with latest O*NET data'
      };

    } catch (error) {
      console.error(`Error updating occupation ${occupationId} from O*NET:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update occupation data from BLS API
   * @param {string} occupationId - Database occupation ID
   * @param {string} socCode - SOC occupation code for BLS lookup
   * @param {string} stateCode - State code (optional)
   */
  async updateOccupationFromBls(occupationId, socCode, stateCode = null) {
    try {
      console.log(`Updating occupation ${occupationId} with BLS data for SOC code ${socCode}`);
      
      // Get BLS data
      const blsResult = await this.blsApi.getComprehensiveOccupationData(socCode, stateCode);
      
      if (!blsResult.success) {
        console.error('Failed to fetch BLS data:', blsResult.error);
        return { success: false, error: blsResult.error };
      }

      // Process BLS data
      const processedData = this.processBlsDataForDatabase(blsResult.data);
      
      // Update database
      const updateResult = await this.updateOccupationInDatabase(occupationId, processedData);
      
      if (!updateResult.success) {
        return updateResult;
      }

      console.log(`Successfully updated occupation ${occupationId} with BLS data`);
      return {
        success: true,
        data: updateResult.data,
        message: 'Occupation updated successfully with latest BLS data'
      };

    } catch (error) {
      console.error(`Error updating occupation ${occupationId} from BLS:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update occupation with both O*NET and BLS data
   * @param {string} occupationId - Database occupation ID
   * @param {string} socCode - SOC occupation code
   * @param {string} stateCode - State code (optional)
   */
  async updateOccupationComprehensive(occupationId, socCode, stateCode = null) {
    try {
      console.log(`Updating occupation ${occupationId} with comprehensive data for SOC code ${socCode}`);
      
      // Get both O*NET and BLS data in parallel
      const [onetResult, blsResult] = await Promise.all([
        this.onetApi.getComprehensiveOccupationData(socCode),
        this.blsApi.getComprehensiveOccupationData(socCode, stateCode)
      ]);

      // Process O*NET data (primary source for detailed occupation info)
      const onetData = onetResult.success ? 
        this.onetApi.processOnetDataForDatabase(onetResult.data) : {};

      // Process BLS data (for employment and wage statistics)
      const blsData = blsResult.success ? 
        this.processBlsDataForDatabase(blsResult.data) : {};

      // Merge the data, with O*NET taking precedence for occupation details
      const mergedData = {
        ...onetData,
        ...blsData,
        // Override with BLS data for employment and wage info
        employment_count: blsData.employment_count || onetData.employment_count,
        employment_trend: blsData.employment_trend || onetData.employment_trend,
        salary_range_min: blsData.salary_range_min || onetData.salary_range_min,
        salary_range_max: blsData.salary_range_max || onetData.salary_range_max,
        // Keep O*NET data for detailed occupation info
        skills_required: onetData.skills_required || [],
        knowledge_areas: onetData.knowledge_areas || [],
        work_activities: onetData.work_activities || [],
        work_context: onetData.work_context || [],
        interests: onetData.interests || [],
        work_values: onetData.work_values || [],
        // Use the best growth outlook data available
        growth_outlook: onetData.growth_outlook || blsData.growth_outlook || 'unknown',
        growth_rate: onetData.job_growth_rate || blsData.growth_rate || null,
        // Metadata
        last_updated: new Date().toISOString(),
        data_sources: {
          onet: onetResult.success,
          bls: blsResult.success
        }
      };

      // Update database
      const updateResult = await this.updateOccupationInDatabase(occupationId, mergedData);
      
      if (!updateResult.success) {
        return updateResult;
      }

      console.log(`Successfully updated occupation ${occupationId} with comprehensive data`);
      return {
        success: true,
        data: updateResult.data,
        message: 'Occupation updated successfully with comprehensive O*NET and BLS data',
        sources: {
          onet: onetResult.success,
          bls: blsResult.success
        }
      };

    } catch (error) {
      console.error(`Error updating occupation ${occupationId} with comprehensive data:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update multiple occupations from BLS
   * @param {Array} occupations - Array of {id, soc_code, state_code} objects
   */
  async updateMultipleOccupations(occupations) {
    const results = [];
    
    for (const occupation of occupations) {
      try {
        const result = await this.updateOccupationFromBls(
          occupation.id, 
          occupation.soc_code, 
          occupation.state_code
        );
        results.push({
          occupationId: occupation.id,
          socCode: occupation.soc_code,
          success: result.success,
          error: result.error
        });
      } catch (error) {
        results.push({
          occupationId: occupation.id,
          socCode: occupation.soc_code,
          success: false,
          error: error.message
        });
      }
    }

    return {
      success: true,
      results,
      summary: {
        total: occupations.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length
      }
    };
  }

  /**
   * Process BLS data for database storage
   * @param {Object} blsData - Raw BLS data
   */
  processBlsDataForDatabase(blsData) {
    const employment = blsData.employment?.data || {};
    const wages = blsData.wages?.data || {};
    const projections = blsData.projections?.data || {};

    // Extract current values
    const currentEmployment = employment.Results?.series?.[0]?.data?.[0]?.value;
    const currentWage = wages.Results?.series?.[0]?.data?.[0]?.value;
    const currentProjection = projections.Results?.series?.[0]?.data?.[0]?.value;

    // Calculate trends
    const employmentTrend = this.calculateTrend(employment.Results?.series?.[0]?.data || []);
    const wageTrend = this.calculateTrend(wages.Results?.series?.[0]?.data || []);
    const growthRate = this.calculateGrowthRate(projections.Results?.series?.[0]?.data || []);

    return {
      // Employment data
      employment_count: currentEmployment,
      employment_trend: employmentTrend,
      
      // Wage data
      salary_range_min: currentWage ? Math.round(currentWage * 0.8) : null, // 80% of median
      salary_range_max: currentWage ? Math.round(currentWage * 1.2) : null, // 120% of median
      
      // Growth projections
      growth_outlook: this.mapGrowthRateToOutlook(growthRate),
      growth_rate: growthRate,
      
      // Metadata
      bls_last_updated: new Date().toISOString(),
      data_source: 'BLS_OEWS'
    };
  }

  /**
   * Update occupation in database
   * @param {string} occupationId - Database occupation ID
   * @param {Object} updateData - Data to update
   */
  async updateOccupationInDatabase(occupationId, updateData) {
    try {
      const { data, error } = await supabase
        .from('occupations')
        .update(updateData)
        .eq('id', occupationId)
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error updating occupation in database:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate trend from data series
   * @param {Array} data - Array of data points
   */
  calculateTrend(data) {
    if (data.length < 2) return 'stable';
    
    const recent = data[0]?.value;
    const previous = data[1]?.value;
    
    if (!recent || !previous) return 'unknown';
    
    const change = ((recent - previous) / previous) * 100;
    
    if (change > 5) return 'growing';
    if (change < -5) return 'declining';
    return 'stable';
  }

  /**
   * Calculate growth rate from projections
   * @param {Array} projections - Array of projection data
   */
  calculateGrowthRate(projections) {
    if (projections.length < 2) return null;
    
    const start = projections[projections.length - 1]?.value;
    const end = projections[0]?.value;
    
    if (!start || !end) return null;
    
    return ((end - start) / start) * 100;
  }

  /**
   * Map growth rate to outlook category
   * @param {number} growthRate - Growth rate percentage
   */
  mapGrowthRateToOutlook(growthRate) {
    if (growthRate === null) return 'unknown';
    
    if (growthRate > 10) return 'very_high';
    if (growthRate > 5) return 'high';
    if (growthRate > 0) return 'moderate';
    if (growthRate > -5) return 'stable';
    return 'declining';
  }

  /**
   * Get occupations that need updating
   * @param {number} daysOld - Number of days old before needing update
   */
  async getOccupationsNeedingUpdate(daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      const { data, error } = await supabase
        .from('occupations')
        .select('id, title, soc_code, bls_last_updated')
        .or(`bls_last_updated.is.null,bls_last_updated.lt.${cutoffDate.toISOString()}`)
        .not('soc_code', 'is', null)
        .limit(50); // Limit to prevent overwhelming the API

      if (error) {
        throw new Error(error.message);
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error getting occupations needing update:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Schedule automatic updates for all occupations
   */
  async scheduleAutomaticUpdates() {
    try {
      console.log('Starting automatic occupation updates...');
      
      // Get occupations that need updating
      const occupationsResult = await this.getOccupationsNeedingUpdate();
      
      if (!occupationsResult.success) {
        return occupationsResult;
      }

      const occupations = occupationsResult.data;
      
      if (occupations.length === 0) {
        return {
          success: true,
          message: 'No occupations need updating at this time'
        };
      }

      console.log(`Found ${occupations.length} occupations needing updates`);

      // Update occupations in batches to respect API limits
      const batchSize = 5; // BLS allows 25 requests per day without API key
      const batches = [];
      
      for (let i = 0; i < occupations.length; i += batchSize) {
        batches.push(occupations.slice(i, i + batchSize));
      }

      const results = [];
      
      for (const batch of batches) {
        const batchResults = await this.updateMultipleOccupations(
          batch.map(occ => ({
            id: occ.id,
            soc_code: occ.soc_code,
            state_code: null
          }))
        );
        
        results.push(batchResults);
        
        // Add delay between batches to respect rate limits
        if (batches.indexOf(batch) < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      return {
        success: true,
        results,
        message: `Processed ${occupations.length} occupations for updates`
      };

    } catch (error) {
      console.error('Error scheduling automatic updates:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new OccupationUpdateService();